const API_URL = import.meta.env.VITE_API_URL || "";

/** Extract accountId from the stored JWT without signature verification (read-only claim). */
function getAccountIdFromToken(): string | null {
  try {
    const token = sessionStorage.getItem("recruit_ai_token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.accountId ? String(payload.accountId) : null;
  } catch {
    return null;
  }
}

// ── Customer list ────────────────────────────────────────────────────────────

export interface CrmCustomer {
  customerId: string;
  name: string;
  userCount: number;
  isTest: boolean;
  isActive: boolean;
}

// ── User list ────────────────────────────────────────────────────────────────

export interface CrmUserActivity {
  messageCount: number;
  threadCount: number;
  outreachDrafted: number;
  outreachSent: number;
  schoolListsCreated: number;
}

export interface CrmUserListItem {
  userId: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  userType: "player" | "coach";
  customer: {
    customerId: string | null;
    name: string | null;
  };
  createdAt: string;
  lastActiveAt: string | null;
  subscriptionState: string;
  hasAccess: boolean;
  activationStage: "activated" | "invited" | "lapsed";
  jgsLinked: boolean;
  ajgaLinked: boolean;
  activity: CrmUserActivity;
}

export interface CrmUsersResponse {
  users: CrmUserListItem[];
  total: number;
  skip: number;
  limit: number;
}

export interface CrmUserFilters {
  customerId?: string;
  search?: string;
  userType?: "player" | "coach";
  subscriptionStatus?: string;
  activationStage?: "activated" | "invited" | "lapsed";
  hasActivity?: "true" | "false";
  jgsLinked?: "true" | "false";
  ajgaLinked?: "true" | "false";
  gradYear?: string;
  homeState?: string;
  milestoneStage?: string;
  createdAfter?: string;
  createdBefore?: string;
  lastActiveAfter?: string;
  lastActiveBefore?: string;
  sortBy?: "createdAt" | "name" | "lastActiveAt" | "messageCount";
  sortDir?: "asc" | "desc";
  hideTest?: "true" | "false";
  skip?: number;
  limit?: number;
}

// ── User detail ──────────────────────────────────────────────────────────────

export interface CrmUserProfile {
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  gradYear: number | null;
  gender: string | null;
  homeState: string | null;
  handedness: string | null;
  gpa: number | null;
  satScore: number | null;
  actScore: number | null;
  preferredMajors: string[];
  cellNumber: string | null;
  instagramProfile: string | null;
  twitterHandle: string | null;
  youtube: string | null;
  swingVideoUrl: string | null;
}

export interface CrmUserDetailActivity {
  messageCount: number;
  threadCount: number;
  lastActiveAt: string | null;
  outreachDrafted: number;
  outreachSent: number;
  outreachByStatus: Record<string, number>;
  schoolListsCreated: number;
  schoolPipelineCount: number;
}

export interface CrmUserMilestones {
  accountCreated: string | null;
  firstSession: string | null;
  jgsLinked: string | null;
  firstMessageSent: string | null;
  firstSchoolList: string | null;
  firstOutreachDrafted: string | null;
  firstOutreachSent: string | null;
}

export interface CrmUserSubscription {
  status: string | null;
  state: string;
  planType: "monthly" | "yearly" | "unknown" | null;
  priceId: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  subscriptionOverrideEnd: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeCustomerUrl: string | null;
}

export interface CrmUserJgsData {
  jgsProfileId: string | null;
  playerName: string | null;
  state: string | null;
  hometown: string | null;
  classYear: number | null;
  rank: number | null;
  scoringDifferential: number | null;
  stateRank: number | null;
  gender: string | null;
  events: number | null;
  studentId: string | null;
}

export interface CrmCoachProfile {
  jgsProfileId: string;
  studentId: string | null;
  displayName: string | null;
  linkedAt: string | null;
}

export interface CrmEmailPreferences {
  engagementEmails: boolean;
  unsubscribedAt: string | null;
  newsletterEmails: boolean;
  newsletterUnsubscribedAt: string | null;
}

export interface CrmUserDetail {
  user: {
    userId: string;
    email: string;
    userType: "player" | "coach";
    customer: {
      customerId: string | null;
      name: string | null;
    };
    createdAt: string;
    subscriptionState: string;
    hasAccess: boolean;
    activationStage: "activated" | "invited" | "lapsed";
    jgsLinked: boolean;
    ajgaLinked: boolean;
  };
  profile: CrmUserProfile;
  activity: CrmUserDetailActivity;
  milestones: CrmUserMilestones;
  jgsData: CrmUserJgsData | null;
  ajgaData: unknown | null;
  coachProfiles: CrmCoachProfile[];
  subscription?: CrmUserSubscription;
  emailPreferences?: CrmEmailPreferences;
}

// ── API functions ────────────────────────────────────────────────────────────

export async function fetchCrmCustomers(): Promise<CrmCustomer[]> {
  const res = await fetch(`${API_URL}/admin/crm/customers`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch CRM customers");
  return res.json();
}

export async function fetchCrmUsers(filters: CrmUserFilters = {}): Promise<CrmUsersResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  const query = params.toString();
  const res = await fetch(`${API_URL}/admin/crm/users${query ? `?${query}` : ""}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch CRM users");
  return res.json();
}

export class HttpError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
  }
}

export async function fetchCrmUserDetail(userId: string): Promise<CrmUserDetail> {
  const res = await fetch(`${API_URL}/admin/crm/users/${userId}`, {
    credentials: "include",
  });
  if (!res.ok) throw new HttpError(res.status, "Failed to fetch CRM user detail");
  return res.json();
}

export interface AdminAccount {
  _id: string;
  email: string;
  roles: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function fetchAdminAccount(accountId: string): Promise<AdminAccount> {
  const res = await fetch(`${API_URL}/admin/accounts/${accountId}`, {
    credentials: "include",
  });
  if (!res.ok) throw new HttpError(res.status, "Failed to fetch account");
  return res.json();
}

export async function grantAccess(userId: string, months: number): Promise<any> {
  const res = await fetch(`${API_URL}/admin/users/${userId}/grant-access`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ months }),
  });
  if (!res.ok) throw new Error("Failed to grant access");
  return res.json();
}

export async function revokeAccess(userId: string): Promise<any> {
  const res = await fetch(`${API_URL}/admin/users/${userId}/revoke-access`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to revoke access");
  return res.json();
}

/**
 * Launch as a user from admin CRM.
 * 1. Impersonate the user's customer
 * 2. Launch as the user (sets actingAs cookie)
 * 3. Navigate to the target path
 */
export async function launchAsUser(
  customerId: string,
  userEmail: string,
  path = "/"
): Promise<string> {
  // Step 1: Impersonate customer
  const impRes = await fetch(`${API_URL}/admin/impersonate/${customerId}`, {
    method: "POST",
    credentials: "include",
  });
  if (!impRes.ok) throw new Error("Failed to impersonate customer");

  // Step 2: Launch as user
  const audience = `${window.location.origin}${path}`;
  const launchRes = await fetch(`${API_URL}/v1/launch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ userEmail, audience }),
  });
  if (!launchRes.ok) throw new Error("Failed to launch as user");
  const data = await launchRes.json();

  // Tag this session as impersonated for analytics (mirrors adminDashboard.ts createLaunchUrl)
  try {
    // Save the admin's userId so analytics keeps the admin as the user.
    // Admin sessions don't set recruit_ai_user_id, so fall back to accountId from the JWT.
    const adminUserId = sessionStorage.getItem("recruit_ai_user_id") || getAccountIdFromToken();
    if (adminUserId) sessionStorage.setItem("recruit_ai_admin_user_id", adminUserId);
    sessionStorage.setItem("recruit_ai_is_impersonated", "1");
    const role = sessionStorage.getItem("recruit_ai_user_role") || "unknown";
    sessionStorage.setItem("recruit_ai_impersonated_by", role);
  } catch {}

  return data.launchUrl || `${window.location.origin}${path}`;
}
